<template>
  <div class="md-layout">
    <div class="md-layout-item">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-primary">
          <div class="card-icon">
            <md-icon>web</md-icon>
          </div>
          <md-toolbar class="fm-card-header">
            <h4 class="md-title">Your Applications</h4>
            <help-link :link="helpLink.link"></help-link>
            <md-button
              class="md-simple"
              @click.native="handleNew()"
              data-tour="application_new"
            >
              <md-icon>add_circle</md-icon>
              New
            </md-button>
          </md-toolbar>
        </md-card-header>
        <md-card-content>
          <loading-spinner
            v-if="loadingApplications"
            title="Applications"
            v-bind:loadingError="loadingError"
          ></loading-spinner>
          <md-table
            v-model="apps"
            v-if="!loadingApplications"
            class="table-striped table-hover"
          >
            <md-table-empty-state
              md-label="No Applications Found"
              md-description="It looks like you have no Applications. Go ahead and create one."
            >
              <md-button
                class="md-primary md-raised"
                @click="handleNew()"
                data-tour="application_create"
              >
                <md-icon>add_circle</md-icon>
                Create
              </md-button>
            </md-table-empty-state>
            <md-table-row
              slot="md-table-row"
              slot-scope="{ item }"
              md-selectable="single"
              @click.stop="handleEdit(item)"
              :data-tour="firstInList(item.id) ? 'application_edit' : false"
            >
              <md-table-cell md-label="Name"
                >{{ item.tag }} {{ appDisabled(item) }}</md-table-cell
              >
              <md-table-cell md-label="Application ID">{{
                item.applicationId
              }}</md-table-cell>
              <md-table-cell md-label="Actions" class="actions">
                <md-button
                  class="md-just-icon md-info md-simple"
                  @click.native.stop="
                    dataTourActive ? false : handleCodePenConnection(item)
                  "
                >
                  <i class="fa fa-codepen" aria-hidden="true"></i>
                  <md-tooltip v-if="!dataTourActive" md-direction="bottom"
                    >Try it out!</md-tooltip
                  >
                </md-button>
                <md-button
                  class="md-just-icon md-danger md-simple"
                  @click.native.stop="
                    dataTourActive ? false : handleDelete(item)
                  "
                >
                  <md-icon>close</md-icon>
                  <md-tooltip v-if="!dataTourActive" md-direction="bottom"
                    >Delete this Application</md-tooltip
                  >
                </md-button>
              </md-table-cell>
            </md-table-row>
          </md-table>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import swal from "sweetalert2";
import { get } from "vuex-pathify";
import AppModel from "@/models/application";
import { getCurrentUser } from "../../utils/auth0";
import {
  generateAppToken,
  dataTourActive,
  appcuesIdentify
} from "../../utils/appcues";
import { changeSelectedApp, isFirstInList } from "../../utils/global";
import { HelpLinkTemplate, LoadingSpinner } from "@/fmComponents";
import helpLinks from "../../contextualHelpLinks.js";

export default {
  components: {
    LoadingSpinner,
    "help-link": HelpLinkTemplate
  },
  data() {
    return {
      appTokens: [],
      loadingApplications: true,
      loadingError: false,
      helpLink: helpLinks.applicationConfig
    };
  },
  computed: {
    apps: get("applications/entities"),
    dataTourActive: () => {
      return dataTourActive;
    }
  },
  created() {
    this.$store
      .dispatch("applications/loadEntities")
      .then(() => {
        this.updateAppTokenList();
        this.loadingApplications = false;
      })
      .catch(ex => {
        this.loadingError = true;
      });
  },
  methods: {
    appDisabled(item) {
      return item.disabled ? "(disabled)" : "";
    },
    firstInList(item) {
      return isFirstInList(item, "applications");
    },
    handleNew() {
      var me = this;
      var newApp = new AppModel();

      swal
        .fire({
          title: `Create Application`,
          input: "text",
          inputValue: newApp.applicationId,
          inputPlaceholder: "Name",
          inputValidator: value => {
            if (!value) {
              return "You must provide a tag!";
            }
          },
          showCancelButton: true,
          confirmButtonClass: "md-button md-success btn-fill",
          cancelButtonClass: "md-button md-danger btn-fill",
          buttonsStyling: false
        })
        .then(result => {
          if (result.value) {
            newApp.tag = result.value;
            newApp.applicationId = me.$store.getters.guidGenerator();
            newApp.sharedSecret = me.$store.getters.sharedSecretGenerator();
            this.$store.dispatch("applications/createEntity", newApp);
            var user = getCurrentUser();
            if (user != undefined && !user.lscAppCreated) {
              user.lscAppCreated = true;
              appcuesIdentify(user.id, {
                // Unique identifier for current user
                LSC_AppCreated: user.lscAppCreated
              });
              this.$store.dispatch("user/updateEntity", user);
            }
            // generate and expose a token to Appcues
            this.apps.push(newApp);
            generateAppToken(newApp.applicationId, newApp.sharedSecret);
            this.updateAppTokenList();
          }
        });
    },
    handleEdit(item) {
      this.$router.push({ path: `/application/${item.id}` });
    },
    updateAppTokenList() {
      this.apps.forEach(app => {
        if (!this.appTokens[app.applicationId]) {
          this.appTokens[app.applicationId] = { value: null };
          generateAppToken(
            app.applicationId,
            app.sharedSecret,
            this.appTokens[app.applicationId]
          );
        }
      });
    },
    handleCodePenConnection(item) {
      if (!dataTourActive) {
        var gatewayUrl = api.props.config.codepenGatewayUrl;
        window.open(
          "https://codepen.io/frznmtn/pen/abbxYpe?token=" +
            this.appTokens[item.applicationId].value +
            "&appId=" +
            item.applicationId +
            "&gwUrl=" +
            gatewayUrl,
          "_blank"
        );
      }
    },
    handleDelete(item) {
      if (!dataTourActive) {
        swal
          .fire({
            title: "Are you sure?",
            text: `You won't be able to revert this!`,
            type: "warning",
            showCancelButton: true,
            confirmButtonClass: "md-button md-success btn-fill",
            cancelButtonClass: "md-button md-danger btn-fill",
            confirmButtonText: "Yes, delete it!",
            buttonsStyling: false
          })
          .then(result => {
            if (result.value) {
              this.$store
                .dispatch("applications/deleteEntity", item.id)
                .then(() => {
                  swal.fire({
                    title: "Deleted!",
                    text: `You deleted ${item.tag}`,
                    type: "success",
                    confirmButtonClass: "md-button md-success btn-fill",
                    buttonsStyling: false
                  });
                });
              changeSelectedApp(null);
            }
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 450px) {
  /deep/ .md-table-head {
    padding: 5px;
  }
  /deep/ .md-table-cell:first-child {
    padding: 5px;
    word-wrap: break-word;
  }
  /deep/ .md-table-cell {
    padding: 0;
    max-width: 61px;
  }
}
</style>
